import { useState, useEffect } from 'react';
import { 
  DashboardStats, 
  HotelMetrics, 
  RecentActivity, 
  AlertSummary, 
  SystemMetrics 
} from '../types/operational';
import { operationalService } from '../services/operationalService';
import { StatsCard } from '../components/operational/StatsCard';
import { RecentActivityList } from '../components/operational/RecentActivityList';
import { AlertsList } from '../components/operational/AlertsList';
import { HotelMetricsTable } from '../components/operational/HotelMetricsTable';
import { SystemMetricsPanel } from '../components/operational/SystemMetricsPanel';

export function OperationalDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [hotelMetrics, setHotelMetrics] = useState<HotelMetrics[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<AlertSummary[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [
        dashboardStats,
        hotelMetricsData,
        activityData,
        alertsData,
        systemMetricsData
      ] = await Promise.all([
        operationalService.getDashboardStats(),
        operationalService.getHotelMetrics(),
        operationalService.getRecentActivity(),
        operationalService.getActiveAlerts(),
        operationalService.getSystemMetrics()
      ]);

      setStats(dashboardStats);
      setHotelMetrics(hotelMetricsData);
      setRecentActivity(activityData);
      setActiveAlerts(alertsData);
      setSystemMetrics(systemMetricsData);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Erreur lors du chargement des données du dashboard');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClick = (alert: AlertSummary) => {
    console.log('Alert clicked:', alert);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Chargement du dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button 
            onClick={fetchDashboardData}
            className="ml-4 text-red-800 underline hover:no-underline"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Opérationnel</h1>
          <p className="text-gray-600 mt-1">
            Vue d'ensemble en temps réel de la plateforme CNS
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
          </div>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualiser
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          <StatsCard
            title="Hôtels Totaux"
            value={stats.totalHotels}
            icon="🏨"
            color="blue"
            trend={{ value: 5.2, isPositive: true }}
          />
          <StatsCard
            title="Invités Actifs"
            value={stats.activeGuests}
            icon="👥"
            color="green"
            trend={{ value: 12.8, isPositive: true }}
          />
          <StatsCard
            title="Alertes Actives"
            value={stats.pendingAlerts}
            icon="⚠️"
            color="red"
            trend={{ value: 3.1, isPositive: false }}
          />
          <StatsCard
            title="Documents à Valider"
            value={stats.documentsToValidate}
            icon="📄"
            color="yellow"
            trend={{ value: 8.5, isPositive: false }}
          />
          <StatsCard
            title="Revenus Totaux"
            value={formatCurrency(stats.totalRevenue)}
            icon="💰"
            color="green"
            trend={{ value: 15.3, isPositive: true }}
          />
          <StatsCard
            title="Taux d'Occupation"
            value={`${stats.occupancyRate.toFixed(1)}%`}
            icon="📊"
            color="purple"
            trend={{ value: 2.7, isPositive: true }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <RecentActivityList activities={recentActivity} />
        </div>
        <div className="lg:col-span-2">
          <AlertsList alerts={activeAlerts} onAlertClick={handleAlertClick} />
        </div>
      </div>

      <HotelMetricsTable metrics={hotelMetrics} />

      {systemMetrics && (
        <SystemMetricsPanel metrics={systemMetrics} />
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="text-2xl mb-2">👤</div>
            <div className="text-sm font-medium">Gérer Invités</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="text-2xl mb-2">🏨</div>
            <div className="text-sm font-medium">Gérer Hôtels</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="text-2xl mb-2">⚙️</div>
            <div className="text-sm font-medium">Paramètres</div>
          </button>
          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="text-2xl mb-2">📊</div>
            <div className="text-sm font-medium">Rapports</div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OperationalDashboardPage;
